<template>
  <div class="verify-logs-container">
    <!-- 页面标题 -->
    <div class="page-header">
      <h2>验证日志</h2>
      <p>查看和管理卡密验证日志记录</p>
    </div>

    <!-- 筛选器 -->
    <div class="filter-section">
      <el-card>
        <el-form :model="filters" inline>
          <el-form-item label="卡密">
            <el-input
              v-model="filters.card_key"
              placeholder="输入卡密"
              clearable
              style="width: 200px"
            />
          </el-form-item>
          
          <el-form-item label="设备ID">
            <el-input
              v-model="filters.device_id"
              placeholder="输入设备ID"
              clearable
              style="width: 200px"
            />
          </el-form-item>
          
          <el-form-item label="验证结果">
            <el-select
              v-model="filters.verify_result"
              placeholder="选择验证结果"
              clearable
              style="width: 150px"
            >
              <el-option label="成功" value="success" />
              <el-option label="失败" value="failed" />
            </el-select>
          </el-form-item>
          
          <el-form-item label="时间范围">
            <el-date-picker
              v-model="filters.dateRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始时间"
              end-placeholder="结束时间"
              format="YYYY-MM-DD HH:mm:ss"
              value-format="YYYY-MM-DD HH:mm:ss"
              style="width: 350px"
            />
          </el-form-item>
          
          <el-form-item>
            <el-button type="primary" @click="searchLogs" :loading="loading">
              <el-icon><Search /></el-icon>
              搜索
            </el-button>
            <el-button @click="resetFilters">
              <el-icon><Refresh /></el-icon>
              重置
            </el-button>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <!-- 统计卡片 -->
    <div class="stats-section">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="stats-card">
            <div class="stats-content">
              <div class="stats-icon success">
                <el-icon><SuccessFilled /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-number">{{ stats.success_count || 0 }}</div>
                <div class="stats-label">成功验证</div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="stats-card">
            <div class="stats-content">
              <div class="stats-icon error">
                <el-icon><CircleCloseFilled /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-number">{{ stats.failed_count || 0 }}</div>
                <div class="stats-label">失败验证</div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="stats-card">
            <div class="stats-content">
              <div class="stats-icon info">
                <el-icon><DataAnalysis /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-number">{{ stats.total_attempts || 0 }}</div>
                <div class="stats-label">总验证次数</div>
              </div>
            </div>
          </el-card>
        </el-col>
        
        <el-col :span="6">
          <el-card class="stats-card">
            <div class="stats-content">
              <div class="stats-icon warning">
                <el-icon><TrendCharts /></el-icon>
              </div>
              <div class="stats-info">
                <div class="stats-number">{{ stats.success_rate || 0 }}%</div>
                <div class="stats-label">成功率</div>
              </div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 日志表格 -->
    <div class="table-section">
      <el-card>
        <template #header>
          <div class="card-header">
            <span>验证日志列表</span>
            <el-button type="primary" size="small" @click="refreshLogs">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </div>
        </template>
        
        <el-table
          :data="logs"
          v-loading="loading"
          stripe
          style="width: 100%"
          :default-sort="{ prop: 'created_at', order: 'descending' }"
        >
          <el-table-column prop="id" label="ID" width="80" />
          
          <el-table-column prop="card_key" label="卡密" width="200">
            <template #default="scope">
              <el-text class="card-key-text" truncated>{{ scope.row.card_key }}</el-text>
            </template>
          </el-table-column>
          
          <el-table-column prop="device_id" label="设备ID" width="180">
            <template #default="scope">
              <el-text truncated>{{ scope.row.device_id }}</el-text>
            </template>
          </el-table-column>
          
          <el-table-column prop="verify_type" label="验证类型" width="120">
            <template #default="scope">
              <el-tag :type="getVerifyTypeColor(scope.row.verify_type)" size="small">
                {{ getVerifyTypeText(scope.row.verify_type) }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="verify_result" label="验证结果" width="100">
            <template #default="scope">
              <el-tag :type="scope.row.verify_result === 'success' ? 'success' : 'danger'" size="small">
                {{ scope.row.verify_result === 'success' ? '成功' : '失败' }}
              </el-tag>
            </template>
          </el-table-column>
          
          <el-table-column prop="error_message" label="错误信息" min-width="200">
            <template #default="scope">
              <span v-if="scope.row.error_message" class="error-message">
                {{ scope.row.error_message }}
              </span>
              <span v-else class="success-message">验证成功</span>
            </template>
          </el-table-column>
          
          <el-table-column prop="ip_address" label="IP地址" width="140" />
          
          <el-table-column prop="created_at" label="验证时间" width="180">
            <template #default="scope">
              {{ formatDateTime(scope.row.created_at) }}
            </template>
          </el-table-column>
          
          <el-table-column label="操作" width="120" fixed="right">
            <template #default="scope">
              <el-button
                type="primary"
                size="small"
                @click="showLogDetail(scope.row)"
              >
                详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
        
        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 日志详情对话框 -->
    <el-dialog
      v-model="detailDialogVisible"
      title="验证日志详情"
      width="800px"
      :close-on-click-modal="false"
    >
      <div v-if="selectedLog" class="log-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="日志ID">{{ selectedLog.id }}</el-descriptions-item>
          <el-descriptions-item label="卡密">{{ selectedLog.card_key }}</el-descriptions-item>
          <el-descriptions-item label="设备ID">{{ selectedLog.device_id }}</el-descriptions-item>
          <el-descriptions-item label="验证类型">
            <el-tag :type="getVerifyTypeColor(selectedLog.verify_type)" size="small">
              {{ getVerifyTypeText(selectedLog.verify_type) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="验证结果">
            <el-tag :type="selectedLog.verify_result === 'success' ? 'success' : 'danger'" size="small">
              {{ selectedLog.verify_result === 'success' ? '成功' : '失败' }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="错误代码">{{ selectedLog.error_code || '-' }}</el-descriptions-item>
          <el-descriptions-item label="IP地址">{{ selectedLog.ip_address }}</el-descriptions-item>
          <el-descriptions-item label="验证时间">{{ formatDateTime(selectedLog.created_at) }}</el-descriptions-item>
        </el-descriptions>
        
        <div class="detail-section" v-if="selectedLog.error_message">
          <h4>错误信息</h4>
          <el-input
            type="textarea"
            :value="selectedLog.error_message"
            :rows="3"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.user_agent">
          <h4>用户代理</h4>
          <el-input
            type="textarea"
            :value="selectedLog.user_agent"
            :rows="2"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.request_data">
          <h4>请求数据</h4>
          <el-input
            type="textarea"
            :value="formatJSON(selectedLog.request_data)"
            :rows="5"
            readonly
          />
        </div>
        
        <div class="detail-section" v-if="selectedLog.response_data">
          <h4>响应数据</h4>
          <el-input
            type="textarea"
            :value="formatJSON(selectedLog.response_data)"
            :rows="5"
            readonly
          />
        </div>
      </div>
      
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { logAPI } from '@/services/api'

export default {
  name: 'VerifyLogs',
  setup() {
    const loading = ref(false)
    const logs = ref([])
    const stats = ref({})
    const detailDialogVisible = ref(false)
    const selectedLog = ref(null)

    const filters = reactive({
      card_key: '',
      device_id: '',
      verify_result: '',
      dateRange: null
    })

    const pagination = reactive({
      page: 1,
      limit: 20,
      total: 0
    })

    // 获取验证日志
    const fetchLogs = async () => {
      try {
        loading.value = true
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...filters
        }

        if (filters.dateRange && filters.dateRange.length === 2) {
          params.start_date = filters.dateRange[0]
          params.end_date = filters.dateRange[1]
        }

        const response = await logAPI.getVerifyLogs(params)
        if (response.success) {
          logs.value = response.data.logs
          pagination.total = response.data.pagination.total
        }
      } catch (error) {
        console.error('获取验证日志失败:', error)
        ElMessage.error('获取验证日志失败')
      } finally {
        loading.value = false
      }
    }

    // 获取统计数据
    const fetchStats = async () => {
      try {
        const response = await logAPI.getVerifyStats({ period: '7d' })
        if (response.success) {
          stats.value = response.data.overall
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    }

    // 搜索日志
    const searchLogs = () => {
      pagination.page = 1
      fetchLogs()
    }

    // 重置筛选器
    const resetFilters = () => {
      Object.keys(filters).forEach(key => {
        filters[key] = key === 'dateRange' ? null : ''
      })
      pagination.page = 1
      fetchLogs()
    }

    // 刷新日志
    const refreshLogs = () => {
      fetchLogs()
      fetchStats()
    }

    // 显示日志详情
    const showLogDetail = (log) => {
      selectedLog.value = log
      detailDialogVisible.value = true
    }

    // 分页处理
    const handleSizeChange = (size) => {
      pagination.limit = size
      pagination.page = 1
      fetchLogs()
    }

    const handleCurrentChange = (page) => {
      pagination.page = page
      fetchLogs()
    }

    // 工具函数
    const formatDateTime = (dateTime) => {
      if (!dateTime) return '-'
      return new Date(dateTime).toLocaleString('zh-CN')
    }

    const formatJSON = (jsonString) => {
      try {
        return JSON.stringify(JSON.parse(jsonString), null, 2)
      } catch {
        return jsonString
      }
    }

    const getVerifyTypeColor = (type) => {
      const colors = {
        'verify': 'primary',
        'heartbeat': 'success',
        'refresh': 'warning'
      }
      return colors[type] || 'info'
    }

    const getVerifyTypeText = (type) => {
      const texts = {
        'verify': '验证',
        'heartbeat': '心跳',
        'refresh': '刷新'
      }
      return texts[type] || type
    }

    onMounted(() => {
      fetchLogs()
      fetchStats()
    })

    return {
      loading,
      logs,
      stats,
      filters,
      pagination,
      detailDialogVisible,
      selectedLog,
      fetchLogs,
      searchLogs,
      resetFilters,
      refreshLogs,
      showLogDetail,
      handleSizeChange,
      handleCurrentChange,
      formatDateTime,
      formatJSON,
      getVerifyTypeColor,
      getVerifyTypeText
    }
  }
}
</script>

<style scoped>
.verify-logs-container {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0 0 8px 0;
  color: #303133;
}

.page-header p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}

.filter-section {
  margin-bottom: 20px;
}

.stats-section {
  margin-bottom: 20px;
}

.stats-card {
  height: 100px;
}

.stats-content {
  display: flex;
  align-items: center;
  height: 100%;
}

.stats-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  font-size: 24px;
  color: white;
}

.stats-icon.success {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stats-icon.error {
  background: linear-gradient(135deg, #f56c6c, #f78989);
}

.stats-icon.info {
  background: linear-gradient(135deg, #409eff, #66b1ff);
}

.stats-icon.warning {
  background: linear-gradient(135deg, #e6a23c, #ebb563);
}

.stats-info {
  flex: 1;
}

.stats-number {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stats-label {
  font-size: 14px;
  color: #909399;
}

.table-section {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-key-text {
  font-family: 'Courier New', monospace;
  font-size: 12px;
}

.error-message {
  color: #f56c6c;
}

.success-message {
  color: #67c23a;
}

.pagination-wrapper {
  margin-top: 20px;
  text-align: right;
}

.log-detail {
  max-height: 600px;
  overflow-y: auto;
}

.detail-section {
  margin-top: 20px;
}

.detail-section h4 {
  margin: 0 0 10px 0;
  color: #303133;
  font-size: 14px;
}
</style>
